import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, checkAuthStatus as apiCheckAuthStatus } from '../services/apiService';
import { CircularProgress, Box } from '@mui/material';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isLoggedIn: false,
    isAdmin: false,
    username: null,
    isLoading: true, // Start loading until checked
  });

  const checkStatus = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await apiCheckAuthStatus();
      if (response.data.loggedIn) {
        setAuthState({
          isLoggedIn: true,
          isAdmin: response.data.isAdmin || false,
          username: response.data.username || null,
          isLoading: false,
        });
      } else {
        setAuthState({ isLoggedIn: false, isAdmin: false, username: null, isLoading: false });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthState({ isLoggedIn: false, isAdmin: false, username: null, isLoading: false });
    }
  }, []);

  useEffect(() => {
    checkStatus(); // Check on initial load
  }, [checkStatus]);

  const login = async (username, password) => {
    try {
      const response = await apiLogin(username, password);
      if (response.data && response.data.message === 'Login successful.') {
         setAuthState({
           isLoggedIn: true,
           isAdmin: response.data.isAdmin || false,
           username: response.data.username || null,
           isLoading: false,
         });
        return { success: true };
      } else {
          // Should not happen if API returns 200 only on success
          // API returns 401 for bad creds, caught below
         return { success: false, message: response.data.error || 'Login failed.' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.error || 'Login failed. Network or server error.';
       setAuthState({ isLoggedIn: false, isAdmin: false, username: null, isLoading: false });
      return { success: false, message: message };
    }
  };

  const logout = async () => {
     setAuthState(prev => ({ ...prev, isLoading: true }));
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Still log out on the frontend even if backend fails
    } finally {
       setAuthState({ isLoggedIn: false, isAdmin: false, username: null, isLoading: false });
    }
  };

  // Show loading spinner while checking auth status initially
  if (authState.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, checkAuthStatus: checkStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
